import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { fecha, gramos } from "../lib/formato";
import { Aviso, Cargando, Etiqueta, Panel, Vacio } from "../componentes/piezas";
import { useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

interface LineaReserva {
  id: string;
  cantidadMg: number;
  producto: { nombre: string };
}

interface Reserva {
  id: string;
  numero: number;
  estado: "PENDIENTE" | "PREPARADA" | "ENTREGADA" | "CANCELADA" | "CADUCADA";
  fechaRecogida: string | null;
  caducaEn: string;
  notas: string | null;
  motivoCancelacion: string | null;
  createdAt: string;
  totalMg: number;
  lineas: LineaReserva[];
}

const CLASE: Record<Reserva["estado"], string> = {
  PENDIENTE: "azul",
  PREPARADA: "verde",
  ENTREGADA: "gris",
  CANCELADA: "roja",
  CADUCADA: "ambar",
};

/** Lo que el socio ha pedido y en qué punto esta. */
export default function PortalReservas() {
  const cliente = useQueryClient();
  const { t, delFallo } = useIdioma();
  const [error, setError] = useState("");

  const reservas = useQuery({
    queryKey: ["portal-reservas"],
    queryFn: () => api<Reserva[]>("/reservas/mias"),
  });

  async function cancelar(id: string) {
    setError("");
    try {
      await api(`/reservas/mias/${id}/cancelar`, { metodo: "POST" });
      void cliente.invalidateQueries({ queryKey: ["portal-reservas"] });
    } catch (fallo) {
      setError(delFallo(fallo, "reservas.falloCancelar"));
    }
  }

  if (reservas.isLoading || !reservas.data)
    return <Cargando texto={t("general.cargando")} />;

  return (
    <div className="columna" style={{ gap: 12 }}>
      <Aviso tipo="error">{error}</Aviso>

      {reservas.data.length === 0 ? (
        <Vacio>{t("reservas.vacio")}</Vacio>
      ) : (
        reservas.data.map((r) => {
          const abierta = r.estado === "PENDIENTE" || r.estado === "PREPARADA";
          // Cancelada no lleva explicación: la trae el motivo, si lo hay.
          const explica =
            r.estado === "CANCELADA"
              ? ""
              : t(`reservas.${r.estado}.explica` as Clave);
          return (
            <Panel
              key={r.id}
              titulo={t("reservas.titulo", { numero: r.numero })}
            >
              <div className="fila separada" style={{ marginBottom: 8 }}>
                <Etiqueta tipo={CLASE[r.estado]}>
                  {t(`reservas.${r.estado}` as Clave)}
                </Etiqueta>
                <span className="suave pequeno">{fecha(r.createdAt)}</span>
              </div>

              {explica && (
                <p className="suave pequeno" style={{ marginTop: 0 }}>
                  {explica}
                </p>
              )}

              <ul style={{ margin: "0 0 8px", paddingLeft: 18 }}>
                {r.lineas.map((l) => (
                  <li key={l.id}>
                    {l.producto.nombre} ·{" "}
                    <span className="tabular">{gramos(l.cantidadMg)}</span>
                  </li>
                ))}
              </ul>

              <div className="fila separada">
                <strong className="tabular">
                  {t("reservas.total", { cantidad: gramos(r.totalMg) })}
                </strong>
                {abierta && (
                  <button
                    className="boton secundario"
                    onClick={() => void cancelar(r.id)}
                  >
                    {t("general.cancelar")}
                  </button>
                )}
              </div>

              {abierta && (
                <p className="suave pequeno" style={{ marginBottom: 0 }}>
                  {t("reservas.guardadaHasta", { fecha: fecha(r.caducaEn) })}
                </p>
              )}
              {r.motivoCancelacion && (
                <p className="suave pequeno" style={{ marginBottom: 0 }}>
                  {r.motivoCancelacion}
                </p>
              )}
            </Panel>
          );
        })
      )}
    </div>
  );
}
